import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ReportWidgetTemplateConfig } from '../../report-widget-template.model';
import { ReportWidgetTemplateRenderApi } from '../../report-widget-template-render-api.model';

@Component({
  selector: 'app-report-widget-ranked5list-template',
  templateUrl: './ranked5list-template.component.html',
  styleUrls: ['./ranked5list-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full min-h-0 min-w-0' },
  standalone: false
})
export class ReportWidgetRanked5ListTemplateComponent {
  @Input() rows: Record<string, any>[] = [];
  @Input() config: ReportWidgetTemplateConfig | null = null;
  @Input() api!: ReportWidgetTemplateRenderApi;
  @Input() highlightFirst = true;

  topListRowStyle(row: Record<string, any> | null, rowIndex?: number): Record<string, string> {
    const style = { ...(this.api?.topListRowStyle(row) || {}) };
    const backgroundColor = this.getRowBackgroundColor(style);
    const highlighted = this.isHighlightedRow(rowIndex);
    if (!backgroundColor || this.isTransparentStyleColor(backgroundColor)) {
      return highlighted
        ? { ...style, border: style.border || '2px solid rgba(15, 23, 42, 0.16)' }
        : style;
    }

    const shadePercent = this.normalizeRankBadgeShadePercent(this.config?.rankBadgeTone?.shadePercent);
    const borderColor = this.deriveRankBadgeToneColor(backgroundColor, shadePercent + 5) || backgroundColor;
    return {
      ...style,
      border: highlighted ? `2px solid ${borderColor}` : (style.border || `1px solid ${borderColor}`)
    };
  }

  isHighlightedRow(rowIndex: number | null | undefined): boolean {
    return this.highlightFirst === true && rowIndex === 0;
  }

  rankBadgeStyle(row: Record<string, any> | null): Record<string, string> {
    const backgroundColor = this.getRowBackgroundColor(this.api?.topListRowStyle(row) || {});
    const rankBadgeTone = this.config?.rankBadgeTone || {};
    if (rankBadgeTone.enabled !== true || !backgroundColor || this.isTransparentStyleColor(backgroundColor)) {
      return {};
    }

    const shadePercent = this.normalizeRankBadgeShadePercent(rankBadgeTone.shadePercent);
    if (shadePercent <= 0) {
      return {};
    }

    const derivedColor = this.deriveRankBadgeToneColor(backgroundColor, shadePercent);
    if (!derivedColor) {
      return {};
    }

    const borderColor = this.deriveRankBadgeToneColor(backgroundColor, shadePercent + 5) || derivedColor;
    return {
      'background-color': derivedColor,
      'border': `1px solid ${borderColor}`
    };
  }

  private getRowBackgroundColor(style: Record<string, string> | null | undefined): string {
    return String(style?.['background-color'] || style?.['backgroundColor'] || '').trim();
  }

  rankAccentStyle(row: Record<string, any> | null): Record<string, string> {
    return this.api?.accentStyle(row) || {};
  }

  hasRankAccentStyle(row: Record<string, any> | null): boolean {
    return Object.keys(this.rankAccentStyle(row)).length > 0;
  }

  trackRow(index: number, row: Record<string, any>): string {
    const id = row?.['id'] || row?.['Id'] || row?.['trackItemId'] || row?.['TrackItemId'];
    return id ? `${id}` : `${index}`;
  }

  trackDecoration = (index: number, item: any): string => {
    return this.api?.trackDecoration(index, item) || `${index}`;
  };

  private normalizeRankBadgeShadePercent(value: unknown): number {
    const numericValue = Number(value ?? 12);
    if (!Number.isFinite(numericValue)) {
      return 12;
    }

    return Math.max(0, Math.min(30, Math.round(numericValue)));
  }

  private deriveRankBadgeToneColor(backgroundColor: string, shadePercent: number): string {
    const rgb = this.parseCssColorToRgb(backgroundColor);
    if (!rgb) {
      return '';
    }

    const hsl = this.rgbToHsl(rgb.r, rgb.g, rgb.b);
    const shadePoints = shadePercent / 3;
    const nextLightness = Math.max(0, hsl.l - shadePoints);
    const nextRgb = this.hslToRgb(hsl.h, hsl.s, nextLightness);
    return `rgb(${nextRgb.r}, ${nextRgb.g}, ${nextRgb.b})`;
  }

  private parseCssColorToRgb(color: string): { r: number; g: number; b: number } | null {
    const value = String(color || '').trim();
    const hexMatch = value.match(/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
    if (hexMatch) {
      const hex = hexMatch[1];
      const parts = hex.length <= 4
        ? [hex[0] + hex[0], hex[1] + hex[1], hex[2] + hex[2]]
        : [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6)];
      return {
        r: parseInt(parts[0], 16),
        g: parseInt(parts[1], 16),
        b: parseInt(parts[2], 16)
      };
    }

    const rgbMatch = value.match(/^rgba?\(([^)]+)\)$/i);
    if (rgbMatch) {
      const parts = rgbMatch[1].split(/[\s,\/]+/).filter(Boolean);
      if (parts.length >= 3) {
        return {
          r: this.parseCssColorChannel(parts[0]),
          g: this.parseCssColorChannel(parts[1]),
          b: this.parseCssColorChannel(parts[2])
        };
      }
    }

    const hslMatch = value.match(/^hsla?\(([^)]+)\)$/i);
    if (hslMatch) {
      const parts = hslMatch[1].split(/[\s,\/]+/).filter(Boolean);
      if (parts.length >= 3) {
        return this.hslToRgb(
          Number.parseFloat(parts[0]),
          this.parseCssPercent(parts[1]),
          this.parseCssPercent(parts[2])
        );
      }
    }

    return null;
  }

  private parseCssColorChannel(value: string): number {
    const text = String(value || '').trim();
    const numericValue = Number.parseFloat(text);
    if (!Number.isFinite(numericValue)) {
      return 0;
    }

    return Math.max(0, Math.min(255, Math.round(text.endsWith('%') ? numericValue * 2.55 : numericValue)));
  }

  private parseCssPercent(value: string): number {
    const numericValue = Number.parseFloat(String(value || '').trim());
    return Number.isFinite(numericValue) ? Math.max(0, Math.min(100, numericValue)) : 0;
  }

  private rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
    const normalizedR = r / 255;
    const normalizedG = g / 255;
    const normalizedB = b / 255;
    const max = Math.max(normalizedR, normalizedG, normalizedB);
    const min = Math.min(normalizedR, normalizedG, normalizedB);
    const lightness = (max + min) / 2;
    if (max === min) {
      return { h: 0, s: 0, l: lightness * 100 };
    }

    const delta = max - min;
    const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    let hue = 0;
    if (max === normalizedR) {
      hue = (normalizedG - normalizedB) / delta + (normalizedG < normalizedB ? 6 : 0);
    } else if (max === normalizedG) {
      hue = (normalizedB - normalizedR) / delta + 2;
    } else {
      hue = (normalizedR - normalizedG) / delta + 4;
    }

    return { h: hue * 60, s: saturation * 100, l: lightness * 100 };
  }

  private hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
    const hue = ((h % 360) + 360) % 360;
    const saturation = Math.max(0, Math.min(100, s)) / 100;
    const lightness = Math.max(0, Math.min(100, l)) / 100;
    const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
    const x = chroma * (1 - Math.abs((hue / 60) % 2 - 1));
    const match = lightness - chroma / 2;
    let r = 0;
    let g = 0;
    let b = 0;
    if (hue < 60) {
      r = chroma; g = x;
    } else if (hue < 120) {
      r = x; g = chroma;
    } else if (hue < 180) {
      g = chroma; b = x;
    } else if (hue < 240) {
      g = x; b = chroma;
    } else if (hue < 300) {
      r = x; b = chroma;
    } else {
      r = chroma; b = x;
    }

    return {
      r: Math.round((r + match) * 255),
      g: Math.round((g + match) * 255),
      b: Math.round((b + match) * 255)
    };
  }

  private isTransparentStyleColor(color: string | null | undefined): boolean {
    const normalized = `${color || ''}`.replace(/\s+/g, '').toLowerCase();
    return normalized === 'transparent'
      || /^#[0-9a-f]{3}0$/.test(normalized)
      || /^#[0-9a-f]{6}00$/.test(normalized)
      || normalized === 'rgba(0,0,0,0)'
      || normalized === 'rgba(255,255,255,0)'
      || /^(rgba|hsla)\([^)]*,0(?:\.0+)?\)$/.test(normalized);
  }
}
